import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import dayjs from "dayjs";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import ControlledFilePicker from "@components/inputs/controlledFilePicker";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";
import RemarkDialog from "@components/dialog/remarkDialog";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints, AssetUrl } from "@configuration/apiEndpoints";

import { formatDateTime } from "@utils/formatter";

import { getFileExtension } from "@utils/helper";

import { ACTION } from "@utils/constant";

const schema = z.object({
  fund: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.string().min(1, "Value is required"),
  }, {invalid_type_error: 'Fund Name is required'}),
  tinNo: z.string().optional(),
  tinPartyFullName: z.string().optional(),

  paymentMethod: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.string().min(1, "Value is required"),
  }, {invalid_type_error: 'Payment Method is required'}),
  bank: z.object({
    bankName: z.string().min(1, "Label is required"),
    bankGuid: z.string().min(1, "Value is required"),
  }, {invalid_type_error: 'Post Name is required'}),
  paymentDate:z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Apply Start Date is required'}))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),
  document:  z
  .custom((value) => value instanceof File, {
    message: "A valid file is required",
  })
  .refine(
    (file) => file.size <= 5 * 1024 * 1024, // Max size: 5MB
    { message: "File size must be less than 5MB" }
  )
  .refine(
    (file) => ["image/jpeg", "image/png", "application/pdf"].includes(file.type),
    { message: "File type must be JPEG, PNG, or PDF" }
  ),

  name: z.string().min(1, { message: 'Name is required' }),
  email: z.string().min(1, { message: 'Email Address is required' }).email("Invalid email address"),
  amount: z.union([
    z.string().regex(/^\d+(\.\d{1,2})?$/, "Amount must be a valid number with up to 2 decimal places"),
    z.number().positive("Amount must be a positive number"),
  ])
  .refine(
    (value) =>
      typeof value === "number" || (typeof value === "string" && parseFloat(value) > 0),
    { message: "Amount must be a positive value" }
  ),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
  paymentNo: z.string().optional(),
  paymentStatus: z.string().optional(),
  paymentGatewayRef: z.string().optional(),

  createdBy: z.string().optional(),
  createdDate: z.string().optional(),
  verifiedBy: z.string().optional(),
  verifiedDate: z.string().optional(),
  verifiedRemark: z.string().optional(),
  voidBy: z.string().optional(),
  voidDate: z.string().optional(),
  voidRemark: z.string().optional(),
})
.refine(
  (data) => {
    // If one is filled, the other must also be filled
    if (data.tinNo || data.tinPartyFullName) {
      return data.tinNo && data.tinPartyFullName;
    }
    return true; // If both are empty, it's valid
  },
  {
    message: "Both TIN No and Party Full Name must be filled if one is entered.",
    path: ["tinNo"], // The error will be associated with 'tinNo'
  }
);

const donationPaymentMethodOptions = [{
  "value": "ed5f48cf-fad3-4a1c-8e16-a352197e0427",
  "label": "Bank Transfer"
}]

export default function DonationPaymentForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user, isCurrentModuleAdmin } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch, setValue } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      paymentMethod: { "value": "ed5f48cf-fad3-4a1c-8e16-a352197e0427","label": "Bank Transfer" }
    },
  });

  const [paymentData, setPaymentData] = useState(null)

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [openRemarkDialog, setOpenRemarkDialog] = useState(false)
  const [remarkDialogAction, setRemarkDialogAction] = useState("")

  useEffect(()=>{
    if (((pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchPaymentMutation.mutate(id)
    }
  },[pageAction])

  const fetchPaymentMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getDonationPayment}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      data.tinNo = data?.tinNo || '-'
      data.tinPartyFullName = data?.tinPartyFullName || '-'
      data.fund = {label: data.fund?.titleEng, value: data.fund?.parameterGuid}
      data.name = data.customer?.customerName
      data.email = data.customer?.emailAddress
      data.contactNumber = data.customer?.contactNumber
      data.paymentMethod = {label: data?.paymentMethod?.valueEng, value: data?.paymentMethod?.parameterGuid}
      data.paymentStatus = data.paymentStatus?.valueEng
      data.verifiedBy = data.verifiedBy || '-'
      data.verifiedDate = data.verifiedDate ? formatDateTime(data.verifiedDate) : '-'
      data.verifiedRemark = data.verifiedRemark || '-'
      data.voidBy = data.voidBy || '-'
      data.voidDate = data.voidDate ? formatDateTime(data.voidDate) : '-'
      data.voidRemark = data.voidRemark || '-'
      setPaymentData(data)
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve payment", "error");
    }
  });

  // const bankOptions = useQuery({
  //   queryKey: ['getBankList'],
  //   queryFn: () =>
  //     axios.get(AdminApiEndpoints.getBankList),
  //   initialData: [],
  //   select: (res) => {
  //     return res?.data || [];
  //   },
  // })

  const fundOptions = useQuery({
    queryKey: ['getFund'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getFundForPayment),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        value: item.fundGuid,
        label: item.fundName,
        banks: item.fundBanks.map(item => ({
          ...item.bank
        }))
    }));
    },
  })

  const createPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createDonationPayment, data, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getDonationPayment'] })
      openSnackbar("Successfully created payment", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create payment", "error");
    }
  })

  const verifyPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.verifyDonationPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getDonationPayment'] })
      openSnackbar("Successfully verified payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to verify payment", "error");
    }
  })

  const voidPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.voidDonationPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getDonationPayment'] })
      openSnackbar("Successfully voided payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to void payment", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();

    const formData = new FormData()
    formData.append('paymentMethodGuid', data?.paymentMethod.value)
    formData.append('bankGuid', data?.bank?.bankGuid)
    formData.append('tinNo', data?.tinNo)
    formData.append('tinPartyFullName', data?.tinPartyFullName)
    formData.append('paymentDate', dayjs(data.paymentDate).format("YYYY-MM-DD"))
    formData.append('payslip', data?.document)
    formData.append('fundGuid', data?.fund?.value)
    formData.append('amount', data?.amount)
    formData.append('customer.customerName', data?.name)
    formData.append('customer.emailAddress', data?.email)
    formData.append('customer.contactNumber', data?.contactNumber)

    formData.append('by', user?.username)
    if(pageAction === "create")
      createPaymentMutation.mutate(formData)
  }

  const onConfirmRemark = (data) => {
    if(remarkDialogAction === ACTION.VERIFY)
      verifyPaymentMutation.mutate({verifiedRemark: data?.remark, veriedBy: user?.username})
    else if(remarkDialogAction === ACTION.VOID)
      voidPaymentMutation.mutate({voidRemark: data?.remark, voidBy: user?.username})
  }

  const onBack = () => {
    navigate('/admin/donation-payment', { replace: true });
  }

  const watchFund = watch('fund')

  const bankOptions = watchFund?.banks || []

  useEffect(()=>{
    if(pageAction === "create")
      setValue('bank', null)
  },[watchFund, pageAction])

  const downloadFile = () => {
    const link = document.createElement("a");
    link.href = `${AssetUrl}${paymentData?.payslipPath}`;
    link.download = `${paymentData?.paymentNo}.${getFileExtension(paymentData?.payslipPath)}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (!fetchPaymentMutation.isPending && !fetchPaymentMutation.isError && ["view"].includes(pageAction)) && isCurrentModuleAdmin &&
          <div style={{display: 'flex', gap: "10px"}}>
            <CustomButton color="#4DB6AC" disabled={paymentData?.paymentStatus !== "Submitted"} onClick={()=>{setRemarkDialogAction(ACTION.VERIFY);setOpenRemarkDialog(true)}}>
              Verify
            </CustomButton>
            <CustomButton 
              color="#6C757D" 
              disabled={!paymentData?.payslipPath} 
              component="a" 
              href={`${AssetUrl}${paymentData?.payslipPath}`} 
              download={`${paymentData?.paymentNo}.${getFileExtension(paymentData?.payslipPath)}`}
              target="_blank"
               rel="noopener noreferrer"
            >
              Download
            </CustomButton>
            <CustomButton color="#D13F58" disabled={paymentData?.createdBy === "SYS" || paymentData?.paymentStatus === "Voided"} onClick={()=>{setRemarkDialogAction(ACTION.VOID);setOpenRemarkDialog(true)}}>
              Void
            </CustomButton>
          </div>
        }
      />
      <SkeletonWrapper isLoading={fetchPaymentMutation.isPending} isError={fetchPaymentMutation.isError} errorMessage="Unable to load payment" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Payment Details</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledAutocomplete 
                name="fund"
                control={control}
                label="Fund Name"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                loading={fundOptions.isFetching}
                options={fundOptions.data}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="tinNo"
                control={control}
                label="TIN No"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="tinPartyFullName"
                control={control}
                label="TIN Party Fullname"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Donator Details</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="name"
                control={control}
                label="Name"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="email"
                control={control}
                label="Email Address"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="contactNumber"
                control={control}
                label="Contact Number"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="amount"
                control={control}
                label="Donate Amount (RM)"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
                type="number"
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Payment Method</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="paymentMethod"
                control={control}
                label="Payment Method"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                options={donationPaymentMethodOptions}
                // loading={donationPaymentMethodOptions.isFetching}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="bank"
                control={control}
                label="Bank"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                options={bankOptions}
                getOptionLabel={(option) => `${option.bankName} - ${option.accountNumber}`}
                getOptionKey={(option) => option.bankGuid}
                isOptionEqualToValue={(option, value) => option.bankGuid === value?.bankGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="paymentDate"
                control={control}
                label="Payment Date"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            {["create"].includes(pageAction) && <>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledFilePicker 
                name="document"
                control={control}
                label="Upload Document"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                type="file"
              />
            </Grid2>
            </>}

            {["view"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentNo"
                control={control}
                label="Payment No"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentStatus"
                control={control}
                label="Payment Status"
                disabled={true}
              />
            </Grid2>

            {/* <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentGatewayRef"
                control={control}
                label="Payment Gateway Ref"
                disabled={true}
              />
            </Grid2> */}
            </>}

          </Grid2>
        </Paper>
        {["view"].includes(pageAction) && <>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Other Details</Typography>
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created Date"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="verifiedBy"
                control={control}
                label="Verified By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="verifiedDate"
                control={control}
                label="Verified Date"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="verifiedRemark"
                control={control}
                label="Verified Remark"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="voidBy"
                control={control}
                label="Void By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="voidDate"
                control={control}
                label="Void Date"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="voidRemark"
                control={control}
                label="Void Remark"
                readOnly={true}
              />
            </Grid2>

          </Grid2>
        </Paper>
        </>}
        {["create"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit payment?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createPaymentMutation.isPending}
      />
      <RemarkDialog 
        open={openRemarkDialog}
        onClose={()=>setOpenRemarkDialog(false)}
        dialogTitle={`${remarkDialogAction} Application`}
        onSubmit={onConfirmRemark}
        loading={verifyPaymentMutation.isPending || voidPaymentMutation.isPending}
      />
    </div>
  );
}
